import { useQueryClient } from '@tanstack/react-query';

import { usePolling } from '~src/lib/hooks/usePolling';
import { ReportApiInstance, SomSearchApiInstance } from '~src/main/api';

import {
  fetchShipmentMetadata,
  fetchTrackingEvents,
  useShipmentMetadataQueryKey,
  useShipmentQueryKey,
  useShipmentTrackingEventQueryKey,
} from './queries';

export function useShipmentMetadataPolling() {
  const metadataQueryKey = useShipmentMetadataQueryKey();
  const queryKey = useShipmentQueryKey();
  const queryClient = useQueryClient();

  return usePolling(
    fetchShipmentMetadata,
    {
      onSuccess: (data, { variables: id }) => {
        void queryClient.setQueryData(metadataQueryKey.detail(id), data);
        void queryClient.invalidateQueries({ queryKey: queryKey.lists() });
        void queryClient.invalidateQueries({ queryKey: queryKey.detail(id) });
      },
    },
    { retryOnError: true }
  );
}

export function useTrackingEventsPolling() {
  const queryKey = useShipmentTrackingEventQueryKey();
  const queryClient = useQueryClient();

  return usePolling(fetchTrackingEvents, {
    onSuccess: (data, { variables: deliveryIds }) => {
      queryClient.setQueryData(queryKey.lists(deliveryIds), data);
    },
  });
}

export function useShipmentReportPolling() {
  const mutationFn = async (reportId: string) => {
    const { data } = await ReportApiInstance.shipmentsGet.reportsGetShipments({ id: reportId });
    return data;
  };

  return usePolling(mutationFn, undefined, {
    throwAfterMaxRetries: true,
    /**
     * The report can take quite some time to generate.
     * Because of that we increase both the poll interval, but also poll retries.
     *
     * 360 * 2.5s = 900 / 60 = 15 minutes
     */
    maxRetries: 360,
    interval: 2_500,
  });
}

export function useShipmentListPolling() {
  const queryKey = useShipmentQueryKey();
  const queryClient = useQueryClient();

  const mutationFn = async (shipmentIds: string[]) => {
    const { data } = await SomSearchApiInstance.shipmentsSummariesGet.somSearchListShipmentsSummaries({
      filtering: { shipment_ids: shipmentIds },
    });
    return data.shipments ?? [];
  };

  return usePolling(mutationFn, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey.lists() });
    },
  });
}
